import { type FunctionComponent } from 'react';
import { type TestProps } from '@direct-frontend/components/testing';
import { type BaseRowData } from '@adv-frontend/data-provider';
import { type GridPlainColumnDefinition, type GridVirtualizationOptions, LegacyGridStub } from '@adv-frontend/grid';
import { type GridSizesStorageKey } from '../../../lib/storage';
import { type AdaptiveSizeConfig, useGridColumnAdaptiveSizing } from '../hooks/useGridColumnAdaptiveSizing';

type UseColumnsHook<Row extends BaseRowData> = (
    params: TestProps & { forStub: true }
) => GridPlainColumnDefinition<Row, string>[];

type BareGridStubSizingConfig = {
    sizes: AdaptiveSizeConfig;
    storageKey: GridSizesStorageKey;
};

type BareGridStubConfig<Row extends BaseRowData> = {
    defaultTestId: string;
    useColumns: UseColumnsHook<Row>;
    virtualization: GridVirtualizationOptions;
    sizing?: BareGridStubSizingConfig;
    stubRowCount?: number;
};

/**
 * Фабрика для создания bare LegacyGridStub компонентов (без GridLayout обёртки).
 *
 * @typeParam Row - Тип строки грида.
 * @returns React-компонент LegacyGridStub с заданной конфигурацией.
 *
 * @kind helper
 */
export function createBareGridStub<Row extends BaseRowData>(
    config: BareGridStubConfig<Row>
): FunctionComponent<TestProps> {
    const { defaultTestId, useColumns, virtualization, sizing, stubRowCount } = config;

    if (sizing) {
        const { sizes, storageKey } = sizing;

        const Component: FunctionComponent<TestProps> = ({ 'data-testid': dataTestId = defaultTestId }) => {
            const columns = useColumns({ 'data-testid': dataTestId, forStub: true });

            const { columnSizing } = useGridColumnAdaptiveSizing({
                storageKey,
                sizes,
                columns,
            });

            return (
                <LegacyGridStub
                    data-testid={dataTestId}
                    columns={columns}
                    virtualization={virtualization}
                    columnSizing={columnSizing}
                    visualOptions={{ borderRadius: '14' }}
                    stubRowCount={stubRowCount}
                />
            );
        };

        Component.displayName = defaultTestId;

        return Component;
    }

    const Component: FunctionComponent<TestProps> = ({ 'data-testid': dataTestId = defaultTestId }) => {
        const columns = useColumns({ 'data-testid': dataTestId, forStub: true });

        return (
            <LegacyGridStub
                data-testid={dataTestId}
                columns={columns}
                virtualization={virtualization}
                visualOptions={{ borderRadius: '14' }}
                stubRowCount={stubRowCount}
            />
        );
    };

    Component.displayName = defaultTestId;

    return Component;
}
